import React, { useState } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { StepButtonBar } from './StepButtonBar';

const PRIMARY = '#c4122f';

interface DeleteStep3PreviewProps {
  selectedRequirements: any[];
  selectedCompanyUnit: any;
  onBack: () => void;
  onCancel: () => void;
  onConfirmDelete: () => Promise<void>;
}

const DeleteStep3Preview: React.FC<DeleteStep3PreviewProps> = ({
  selectedRequirements,
  selectedCompanyUnit,
  onBack,
  onCancel,
  onConfirmDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDeleting, setIsDeleting] = useState(false);
  const { formatMessage } = useIntl();
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirmDelete();
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  };
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {isDeleting && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.72)',
          }}
        >
          <CircularProgress size={40} thickness={4} style={{ color: PRIMARY }} />
        </Box>
      )}
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: '100%',
            padding: isMobile ? theme.spacing(2) : theme.spacing(3),
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            minHeight: '400px',
          }}
        >
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              lineHeight: 1.4,
              fontSize: isMobile ? '16px' : '18px',
            }}
          >
             {formatMessage(
              { id: 'law-portal.delete-req.confirm-title' },
              { unit: selectedCompanyUnit?.name ?? '' }
            )}
          </Typography>

          <Typography
            variant="body2"
            style={{
              marginBottom: theme.spacing(2),
              color: '#555',
              fontSize: '14px',
            }}
          >
            {formatMessage({
              id: 'law-portal.manage.requirements.table.delete.confirmation'            })}
          </Typography>

          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            {selectedRequirements.map((req: any) => (
              <Box
                key={req.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    backgroundColor: PRIMARY,
                    borderRadius: '6px',
                    flexShrink: 0,
                  }}
                >
                  <DescriptionOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                </Box>
                <Typography
                  variant="body2"
                  style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}
                >
                  {req.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </div>
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleDelete}
          nextLabel="law-portal.delete-law.confirm-delete"
          nextDisabled={isDeleting}
          formatMessage={formatMessage}
          showBack={true}
          isDeleteStep={true}
        />
      </Box>
    </div>
  );
};

export default DeleteStep3Preview;
